import { useState } from 'react';
import { useEditor } from '../../state/store';
import { Cell, TRANSPARENT, colorToHex, hexToColor } from '../../core/types';
import { Checkbox, Field, Modal } from '../widgets/common';

type Mode = 'char' | 'color' | 'text';

/** Search and replace across characters, colours, or literal text runs. */
export function SearchReplaceDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const store = useEditor.getState();
  const [mode, setMode] = useState<Mode>('char');
  const [findChar, setFindChar] = useState('');
  const [replaceChar, setReplaceChar] = useState('');
  const [findColor, setFindColor] = useState('#32ff94');
  const [replaceColor, setReplaceColor] = useState('#3ddc97');
  const [channel, setChannel] = useState<'fg' | 'bg'>('fg');
  const [text, setText] = useState('');
  const [selectionOnly, setSelectionOnly] = useState(false);
  const [found, setFound] = useState<string | null>(null);

  const countMatches = (): number => {
    const tab = useEditor.getState().active();
    const grid = tab.doc.composite();
    let n = 0;
    grid.forEachCell((cell, x, y) => {
      if (selectionOnly && !tab.selection.allows(x, y)) return;
      if (matches(cell)) n += 1;
    });
    return n;
  };

  const matches = (cell: Cell): boolean => {
    if (mode === 'char') return findChar !== '' && cell.char === findChar;
    if (mode === 'color') {
      const target = hexToColor(findColor);
      return (channel === 'fg' ? cell.fg : cell.bg) === target;
    }
    return false;
  };

  const replaceAll = () => {
    const state = useEditor.getState();
    const tab = state.active();
    if (mode === 'text') {
      const n = replaceTextRuns(text, replaceChar, selectionOnly);
      state.notify(n > 0 ? `Replaced ${n} occurrence(s).` : 'No matches found.', n > 0 ? 'success' : 'warn');
      return;
    }
    const previousSelection = tab.selection;
    if (!selectionOnly) tab.selection.clear();
    const n = state.replaceInSelection(
      mode === 'char'
        ? { char: findChar }
        : channel === 'fg'
          ? { fg: hexToColor(findColor) }
          : { bg: hexToColor(findColor) },
      mode === 'char'
        ? { char: replaceChar || ' ' }
        : channel === 'fg'
          ? { fg: hexToColor(replaceColor) }
          : { bg: hexToColor(replaceColor) },
    );
    if (!selectionOnly) tab.selection = previousSelection;
    state.notify(n > 0 ? `Replaced ${n} cell(s).` : 'No matches found.', n > 0 ? 'success' : 'warn');
  };

  /** Finds literal text runs on each row and rewrites them in place. */
  const replaceTextRuns = (needle: string, replacement: string, onlySelection: boolean): number => {
    if (!needle) return 0;
    const state = useEditor.getState();
    const tab = state.active();
    const layer = tab.doc.activeLayer;
    const writes: Array<{ x: number; y: number; cell: Cell }> = [];
    let count = 0;

    for (let y = 0; y < tab.doc.height; y++) {
      let row = '';
      for (let x = 0; x < tab.doc.width; x++) {
        const ch = layer.grid.get(x, y).char;
        row += ch === '' ? ' ' : ch;
      }
      let index = row.indexOf(needle);
      while (index >= 0) {
        const inSelection = !onlySelection || tab.selection.allows(index, y);
        if (inSelection) {
          count += 1;
          const style = layer.grid.get(index, y);
          const chars = [...replacement];
          for (let i = 0; i < needle.length; i++) {
            writes.push({
              x: index + i,
              y,
              cell:
                i < chars.length
                  ? { ...style, char: chars[i] }
                  : { char: ' ', fg: style.fg, bg: style.bg, attrs: style.attrs },
            });
          }
          // Text longer than what it replaces overflows to the right.
          for (let i = needle.length; i < chars.length; i++) {
            writes.push({ x: index + i, y, cell: { ...style, char: chars[i] } });
          }
        }
        index = row.indexOf(needle, index + needle.length);
      }
    }
    state.writeCells(writes, 'Replace text');
    return count;
  };

  return (
    <Modal
      title="Search and replace"
      onClose={onClose}
      width={480}
      footer={
        <>
          <button
            className="btn"
            onClick={() => {
              const n = mode === 'text' ? -1 : countMatches();
              setFound(n < 0 ? 'Run replace to apply text changes.' : `${n} match(es).`);
            }}
          >
            Count
          </button>
          <button className="btn" onClick={onClose}>
            Close
          </button>
          <button className="btn primary" onClick={replaceAll}>
            Replace all
          </button>
        </>
      }
    >
      <div className="row" style={{ marginBottom: 10 }}>
        <button className={`btn${mode === 'char' ? ' active' : ''}`} style={{ flex: 1 }} onClick={() => setMode('char')}>
          Character
        </button>
        <button className={`btn${mode === 'color' ? ' active' : ''}`} style={{ flex: 1 }} onClick={() => setMode('color')}>
          Colour
        </button>
        <button className={`btn${mode === 'text' ? ' active' : ''}`} style={{ flex: 1 }} onClick={() => setMode('text')}>
          Text
        </button>
      </div>

      {mode === 'char' && (
        <>
          <Field label="Find">
            <input
              className="input mono"
              value={findChar}
              maxLength={2}
              onChange={(e) => setFindChar([...e.target.value].pop() ?? '')}
            />
          </Field>
          <Field label="Replace with">
            <input
              className="input mono"
              value={replaceChar}
              maxLength={2}
              onChange={(e) => setReplaceChar([...e.target.value].pop() ?? '')}
            />
          </Field>
        </>
      )}

      {mode === 'color' && (
        <>
          <Field label="Channel">
            <div className="row">
              <button className={`btn${channel === 'fg' ? ' active' : ''}`} style={{ flex: 1 }} onClick={() => setChannel('fg')}>
                Foreground
              </button>
              <button className={`btn${channel === 'bg' ? ' active' : ''}`} style={{ flex: 1 }} onClick={() => setChannel('bg')}>
                Background
              </button>
            </div>
          </Field>
          <Field label="Find">
            <div className="row">
              <input
                type="color"
                value={findColor}
                onChange={(e) => setFindColor(e.target.value)}
                style={{ width: 34, height: 26, padding: 0, border: '1px solid var(--border)', borderRadius: 4 }}
              />
              <input className="input mono" value={findColor} onChange={(e) => setFindColor(e.target.value)} />
            </div>
          </Field>
          <Field label="Replace with">
            <div className="row">
              <input
                type="color"
                value={replaceColor}
                onChange={(e) => setReplaceColor(e.target.value)}
                style={{ width: 34, height: 26, padding: 0, border: '1px solid var(--border)', borderRadius: 4 }}
              />
              <input className="input mono" value={replaceColor} onChange={(e) => setReplaceColor(e.target.value)} />
            </div>
          </Field>
          <button
            className="btn"
            style={{ width: '100%', marginBottom: 6 }}
            onClick={() => {
              store.selectByColor(hexToColor(findColor), channel);
              onClose();
            }}
          >
            Select all cells with this colour
          </button>
        </>
      )}

      {mode === 'text' && (
        <>
          <Field label="Find">
            <input className="input mono" value={text} onChange={(e) => setText(e.target.value)} />
          </Field>
          <Field label="Replace with">
            <input className="input mono" value={replaceChar} onChange={(e) => setReplaceChar(e.target.value)} />
          </Field>
          <p className="faint" style={{ lineHeight: 1.5 }}>
            Matches runs of characters on a single row of the active layer, keeping each cell's colours.
          </p>
        </>
      )}

      <Checkbox label="Selection only" checked={selectionOnly} onChange={setSelectionOnly} />
      {found ? (
        <div className="dim" style={{ marginTop: 6 }}>
          {found}
        </div>
      ) : null}
      <span style={{ display: 'none' }}>{colorToHex(TRANSPARENT)}</span>
    </Modal>
  );
}
